import { customizer } from '@consumer/api'
import type { Photo } from '@consumer/types/Photo'
import type { GiftlyDesign, GiftlyMetadata, Place, PlaceInfo } from '@consumer/types/serializers'

// NOTE: Since most of these doesn't change often, we can cache them after the
// first usage.
function cachedComposable<T> (fn: () => Promise<T>) {
  const cachedRef: Ref<T | undefined> & { loaded: Promise<T> } = ref<T>() as any
  let promise: Promise<T> | undefined
  return () => {
    if (import.meta.env.SSR || import.meta.env.MODE === 'test') {
      return cachedRef
    }

    if (!promise) {
      cachedRef.loaded = promise = fn()
    }
    if (!cachedRef.value) {
      promise.then((value) => { cachedRef.value ||= value })
    }
    return cachedRef
  }
}

export const useDefaultLocation = cachedComposable(customizer.defaultLocation<string>)
export const useGeneralPlaces = cachedComposable(customizer.generalPlaces<Place[]>)
export const useGiftlyDesigns = cachedComposable(customizer.giftlyDesigns<GiftlyDesign[]>)

interface SearchParams {
  searchTerms: string
  page?: number
  pageSize?: number
  isMultiplaceBuilder?: boolean
}

interface SearchResults<T> {
  items: T[]
  page: number
  pageSize: number
  total: number
}

export function canViewMore (searchResults: Partial<SearchResults<unknown>>) {
  const { total, page, pageSize } = searchResults
  if (!total || !page || !pageSize) {
    return false
  }
  const totalPages = Math.ceil(total * 1.0 / pageSize)
  return page < totalPages
}

export type DesignSearchResults = SearchResults<Photo> & { provider: 'unsplash' | 'pexels' }

export function getRecommendedDesigns () {
  return customizer.recommendedDesigns<DesignSearchResults>()
}

export function searchDesigns (data: SearchParams) {
  return customizer.searchDesigns<DesignSearchResults>({ data })
}

export type PlaceSearchResults = SearchResults<PlaceInfo>

export function searchPlaces (data: SearchParams & { searchLocation: string }) {
  return customizer.searchPlaces<PlaceSearchResults>({ data })
}

export interface UpdatePurchasedGiftParams {
  giftlyMetadata?: Partial<GiftlyMetadata>
  placeIds?: string[]
}

export function updatePurchasedGift (giftId: string, data: UpdatePurchasedGiftParams) {
  return customizer.updatePurchasedGift({
    params: { id: giftId },
    data,
  })
}
